import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AgentStatus, agentStatus, AgentStatusBadge, agentStatusBadge, AgentV3 } from './frontdoor-v3-agent.service';
import { FdReadout, readoutFootTone } from './frontdoor-v3-dashboard';
import {
    hostHealthBadge,
    HostHealthChip,
    hostHealthChip,
    HostHealthState,
    ShareHealthV3,
    shareHealthView,
} from './frontdoor-v3-share-health';

/** The two roles an agent can host a share in. */
export type ShareHostRole = 'active' | 'passive';

/** The host lists every kind of share carries: TCP, HTTP and IPsec alike. */
export interface ShareHosts {
    activeAgentIds?: string[];
    passiveAgentIds?: string[];
    health?: ShareHealthV3 | null;
}

/** The path the service reports each role's faults under, on every kind of share. */
export const SHARE_HOST_PATHS: Record<ShareHostRole, string> = {
    active: '$.activeAgentIds',
    passive: '$.passiveAgentIds',
};

/** One agent hosting a share, resolved from the agents a page already reads. */
export interface ShareHostChip {
    id: string;
    /** The agent's name, or its id where the agent could not be read. */
    label: string;
    role: ShareHostRole;
    status: AgentStatus;
    badge: AgentStatusBadge;
    /** False where the agent read did not return this agent, such as one deleted since. */
    known: boolean;
    /** Whether the host serves this share, or null where the share has no health for it. */
    health?: HostHealthChip | null;
}

/** One dot of a listing's dot cell: filled for an active role, a ring for a passive one. */
export interface CellDot {
    id: string;
    tone: 'ok' | 'warn' | 'err' | 'muted';
    passive: boolean;
}

/** What a listing's dot cell draws: a dot per item, a label, an aside and a tooltip. A cell with no dots and no label is a dash. */
export interface DotCell {
    dots: CellDot[];
    primary: string;
    extra: string;
    /** One line per item, or why the cell is a dash. */
    tooltip: string;
}

/** What a share listing's Hosting cell draws: a dot per host in its agent's status, the first host's name, the rest by role. */
export interface HostingCell extends DotCell {
    hosts: ShareHostChip[];
}

/** One share an agent hosts, with the kind it is listed under, such as "HTTP". */
export interface AgentHostedShare {
    kind: string;
    share: ShareHosts & { id: string; name?: string; agentId?: string };
}

/** The most dots an agent's Shares cell draws. The count beside them carries the rest. */
export const AGENT_SHARE_DOTS = 8;

/** The most shares an agent's Shares tooltip names. */
export const AGENT_SHARE_TOOLTIP_LINES = 20;

/** Every agent that hosts `share`: the actives first, then the passives. */
export function shareAgentIds(share: ShareHosts | undefined): string[] {
    return [...(share?.activeAgentIds ?? []), ...(share?.passiveAgentIds ?? [])];
}

/** Every host of `share` with its role, the agent's own status and its health for the share, the actives first. */
export function shareHostChips(share: ShareHosts | undefined, agentsById: Map<string, AgentV3>): ShareHostChip[] {
    const chip = (id: string, role: ShareHostRole): ShareHostChip => {
        const agent = agentsById.get(id);
        const status = agent ? agentStatus(agent) : 'unknown';
        return {
            id,
            label: agent?.name || id,
            role,
            status,
            badge: agentStatusBadge(status),
            known: !!agent,
            health: hostHealthChip(share?.health, id, role),
        };
    };
    return [
        ...(share?.activeAgentIds ?? []).map((id) => chip(id, 'active')),
        ...(share?.passiveAgentIds ?? []).map((id) => chip(id, 'passive')),
    ];
}

/** The hosts of a share, counted by role and by those online. */
export interface ShareHostCounts {
    total: number;
    active: number;
    passive: number;
    online: number;
}

export function shareHostCounts(hosts: ShareHostChip[]): ShareHostCounts {
    return {
        total: hosts.length,
        active: hosts.filter((host) => host.role === 'active').length,
        passive: hosts.filter((host) => host.role === 'passive').length,
        online: hosts.filter((host) => host.status === 'online').length,
    };
}

/** The Agents readout's foot line: "2 active · 1 passive · 2 online". */
export function hostsFoot(counts: ShareHostCounts): string {
    return `${counts.active} active · ${counts.passive} passive · ${counts.online} online`;
}

const HOST_HEALTH_ORDER: HostHealthState[] = ['serving', 'failing', 'notBound', 'misconfigured', 'unknown'];

/** The hosts counted by health, such as "2 Serving · 1 Failing", or '' where no host has health. */
export function hostHealthSummary(hosts: ShareHostChip[]): string {
    return HOST_HEALTH_ORDER.map((state) => ({
        state,
        count: hosts.filter((host) => host.health?.state === state).length,
    }))
        .filter(({ count }) => count > 0)
        .map(({ state, count }) => `${count} ${hostHealthBadge(state).label}`)
        .join(' · ');
}

/** A share dashboard's Health readout: the share's health, with its hosts counted by health below it. */
export function shareHealthReadout(share: ShareHosts | null | undefined, hosts: ShareHostChip[]): FdReadout {
    const health = shareHealthView(share);
    return {
        key: 'health',
        label: 'Health',
        value: health?.badge.label ?? '—',
        foot: health ? hostHealthSummary(hosts) || ' ' : 'not reported yet',
        icon: 'pi-heart',
        footTone: health ? readoutFootTone(health.badge.tone) : 'none',
    };
}

/** The hosts of one share, and the agents they were resolved from, to keep for the next read. */
export interface ShareHostsRead {
    hosts: ShareHostChip[];
    agentsById: Map<string, AgentV3>;
}

/**
 * The hosts of `share` once `agents$` answers. A failed read never errors: it resolves the hosts against `lastKnown`,
 * so a page keeps the last statuses it saw, or lists the hosts by id when it saw none.
 */
export function resolveShareHosts(
    share: ShareHosts,
    agents$: Observable<AgentV3[]>,
    lastKnown: Map<string, AgentV3>
): Observable<ShareHostsRead> {
    return agents$.pipe(
        map((agents) => new Map(agents.map((agent) => [agent.id, agent]))),
        catchError(() => of(lastKnown)),
        map((agentsById) => ({ hosts: shareHostChips(share, agentsById), agentsById }))
    );
}

/** What the Hosting cell and the drawer's Agents pane draw for `share`: the first host by name, the rest counted by role. */
export function shareHostingCell(share: ShareHosts | undefined, agentsById: Map<string, AgentV3>): HostingCell {
    const hosts = shareHostChips(share, agentsById);
    const others = hosts.slice(1);
    const extra = (['active', 'passive'] as ShareHostRole[])
        .map((role) => ({ role, count: others.filter((host) => host.role === role).length }))
        .filter(({ count }) => count > 0)
        .map(({ role, count }) => `+${count} ${role}`)
        .join(', ');
    return {
        hosts,
        dots: hosts.map((host) => ({ id: host.id, tone: host.badge.tone, passive: host.role === 'passive' })),
        primary: hosts[0]?.label ?? '',
        extra,
        tooltip: hosts.map(hostTooltipLine).join('\n'),
    };
}

/**
 * What an agent listing's Shares cell draws: a dot per hosted share in this agent's health for it, the worst first, and
 * the count. `partial` marks a count that is only a floor. Null `shares` means the shares could not be read.
 */
export function agentSharesCell(agentId: string, shares: AgentHostedShare[] | null, partial = false): DotCell {
    if (!shares) {
        return { dots: [], primary: '', extra: '', tooltip: 'The shares could not be read' };
    }
    const rows = shares
        .map(({ kind, share }) => {
            const role: ShareHostRole = (share.passiveAgentIds ?? []).includes(agentId) ? 'passive' : 'active';
            return { kind, share, role, health: hostHealthChip(share.health, agentId, role) };
        })
        .sort((a, b) => toneRank(a.health?.badge.tone) - toneRank(b.health?.badge.tone));
    const lines = rows.slice(0, AGENT_SHARE_TOOLTIP_LINES).map(({ kind, share, role, health }) => {
        const state = health?.badge.label ?? 'Health not reported';
        return `${share.name || share.id} (${kind}): ${role === 'active' ? 'Active' : 'Passive'}, ${state}`;
    });
    if (rows.length > AGENT_SHARE_TOOLTIP_LINES) {
        lines.push(`and ${rows.length - AGENT_SHARE_TOOLTIP_LINES} more`);
    }
    return {
        dots: rows.slice(0, AGENT_SHARE_DOTS).map(({ share, role, health }) => ({
            id: share.id,
            tone: health?.badge.tone ?? 'muted',
            passive: role === 'passive',
        })),
        primary: `${rows.length}${partial ? '+' : ''}`,
        extra: '',
        tooltip: lines.join('\n'),
    };
}

const TONE_RANK: Record<CellDot['tone'], number> = { err: 0, warn: 1, ok: 2, muted: 3 };

/** Orders dots worst first, so a failing share stays in view when the dots are capped. */
function toneRank(tone: CellDot['tone'] | undefined): number {
    return TONE_RANK[tone ?? 'muted'];
}

/** "edge-1: Active, Serving · Agent Online", or the agent status alone where the share has no health for the host. */
function hostTooltipLine(host: ShareHostChip): string {
    const role = host.role === 'active' ? 'Active' : 'Passive';
    return host.health
        ? `${host.label}: ${role}, ${host.health.badge.label} · Agent ${host.badge.label}`
        : `${host.label}: ${role}, ${host.badge.label}`;
}
